import { fetchAPI } from './client';

export interface InstanceStatus {
    instanceName: string;
    state: string; // 'connected', 'connecting', 'disconnected', 'qrcode'
}

export interface QrCodeResponse {
    code: string;
    base64: string;
}

/**
 * Cria ou conecta uma sessão (equivalente a /instance/create ou /session/connect)
 * Usa 5 retries com backoff de 2s para sobreviver a períodos de overload do Edge Functions.
 */
export async function connectInstance(instanceName: string): Promise<any> {
    // Primeiro tenta dar init. Se já existe, ele vai falhar e ignoramos. 
    // Em seguida damos connect para de fato gerar o QR ou parear.
    try {
        await fetchAPI(`instance/init`, {
            method: 'POST',
            body: JSON.stringify({ name: instanceName, systemName: 'Adlitics' }),
            headers: { 'instance': instanceName }
        }, 5, 2000);
    } catch(e) { /* init falha se já existe, normal */ }

    // Delay para evitar colisão de requests no Edge Function runtime
    await new Promise(r => setTimeout(r, 1000));

    // WuzAPI usa POST /instance/connect (e não passamos o name na url)
    return fetchAPI(`instance/connect`, {
        method: 'POST',
        headers: { 'instance': instanceName }
    }, 5, 2000);
}

/**
 * Pega o status atual da instância
 */
export async function getInstanceStatus(instanceName: string): Promise<any> {
    // WuzAPI usa GET /instance/status
    return fetchAPI<any>(`instance/status`, {
        method: 'GET',
        headers: { 'instance': instanceName }
    });
}

/**
 * Pega todas as instâncias e seus perfis (para a listagem de conexões)
 * O proxy wrapa arrays em { chats: [...] }, precisa unwrap.
 */
export async function getAllInstances(): Promise<any[]> {
    try {
        const data = await fetchAPI<any>(`instance/all`, { method: 'GET' });
        if (Array.isArray(data)) return data;
        if (data?.chats && Array.isArray(data.chats)) return data.chats;
        return [];
    } catch {
        return [];
    }
}

/**
 * Desconecta a sessão WhatsApp sem apagar a instância (permite reconectar).
 */
export async function logoutInstance(instanceName: string): Promise<any> {
    return fetchAPI(`instance/disconnect`, {
        method: 'POST',
        headers: { 'instance': instanceName }
    });
}

/**
 * Remove permanentemente a instância na UazAPI (DELETE /instance).
 */
export async function removeInstance(instanceName: string): Promise<any> {
    return fetchAPI(`instance`, {
        method: 'DELETE',
        headers: { 'instance': instanceName }
    });
}

/**
 * Obtém o QRCode em Base64 para exibir no frontend
 */
export async function getQrCode(instanceName: string): Promise<QrCodeResponse | null> {
    // WuzAPI não tem rota isolada pra QR, o QR vem no payload de /instance/connect ou /instance/status
    const status = await getInstanceStatus(instanceName);
    
    // Conforme a WuzAPI, o base64 geralmente vem em 'qrcode', 'base64' ou 'pairingCode' na raiz da resposta
    if (status && (status.qrcode || status.base64)) {
        return {
            base64: status.base64 || status.qrcode,
            code: status.pairingCode || status.code
        };
    }
    
    // Se não veio no status, tentamos o connect forçado
    try {
        const connectRes = await fetchAPI<any>(`instance/connect`, {
            method: 'POST',
            headers: { 'instance': instanceName }
        });
        
        if (connectRes && (connectRes.qrcode || connectRes.base64)) {
            return {
                base64: connectRes.base64 || connectRes.qrcode,
                code: connectRes.pairingCode || connectRes.code
            };
        }
    } catch (e) { }

    return null;
}
